import { DateTime } from 'luxon';
import { parse, ParseResult } from 'papaparse';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router';
import { Accordion, Button, Dropdown, Form, Grid, Icon, Input, Label, Message, Modal, Progress } from 'semantic-ui-react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { AppState } from '../../types/app-state';
import { AuthState } from '../../types/auth-state';
import { CurrentCSVSession } from '../../types/currentcsvsession';
import { PricesCSVHeaders } from '../../types/prices-csv-headers';
import { post } from '../../utils/http';
import { DropZone } from '../DropZone/DropZone';
import './NewPricesPage.less';

export type PricesRevisionPageProps = {
	token: string;
} & RouteComponentProps<{}>;

export type PricesRevisionPageState = {
	file: File | null;
	headers: PricesCSVHeaders;
	error: string;
	isSubmitting: boolean;
};

type IEntry = {
	file: File;
	states: string[];
	errors: string[];
}

type RemainingKeys<T> = Exclude<keyof ParseResult<T>, 'data'>;
type ParseResultSingle<T> = Pick<ParseResult<T>, RemainingKeys<T>> & {
	data: T;
};

const segmentSize = 100;

const accepteduom = [
	'D', 
	'EA', 
	'E',
	'H', 
	'MON', 
	'WK', 
	'YR'
];

const stateOptions = [
	{
		key: "AU-NSW",
		value: "AU-NSW",
		text: "New South Wales"
	},
	{
		key: "AU-QLD",
		value: "AU-QLD",
		text: "Queensland"
	},
	{
		key: "AU-SA",
		value: "AU-SA",
		text: "South Australia"
	},
	{
		key: "AU-TAS",
		value: "AU-TAS",
		text: "Tasmania"
	},
	{
		key: "AU-VIC",
		value: "AU-VIC",
		text: "Victoria"
	},
	{
		key: "AU-WA",
		value: "AU-WA",
		text: "Western Australia"
	},
	{
		key: "AU-ACT",
		value: "AU-ACT",
		text: "Australian Capital Territory"
	},
	{
		key: "AU-NT",
		value: "AU-NT",
		text: "Northern Territory"
	}
];

const allStates = stateOptions.map(o => o.value);

export const NewPricesPage: React.FC = () => {
	const history = useHistory();
	const { token } = useSelector<AppState, Partial<AuthState>>(
		(state) => state.auth
	);
	const [current, setCurrent] = useLocalStorage<CurrentCSVSession | null>('price_revision_session', null);
	const [revisionName, setRevisionName] = useState(DateTime.now().toLocaleString(DateTime.DATETIME_FULL));
	const [entries, setEntries] = useState<IEntry[]>([]);
	const [activeIndex, setActiveIndex] = useState(0);
	const [showHeaders, setShowHeaders] = useState(false);
	const [error, setError] = useState('');
	const [headers, setHeaders] = useState({
		external_id: 'Support Item Number',
		name: 'Support Item',
		description: 'Support Item Description',
		unit_of_measurement: 'Unit of Measure',
		quote: 'Quote',
		ndis_price: 'Price Limit',
		category: 'Support Categories',
		support_area_id: 'Support Category Number',
	});
	const cancelled = useRef(false);

	// this is the uploader
	useEffect(() => {
		if (current && !current.complete && !current.error && !cancelled.current) {
			if (current.state < current.size) {
				const segmentData = window.localStorage.getItem(current.id + '_' + current.state);
				if (segmentData && token) {
					post(
						`1.0/admin/prices-revision/${current.id}/segment/${current.state}`, 
						{ token, data: segmentData }
					).then((result) => {
						if (result.status === 1) {
							window.localStorage.removeItem(current.id + '_' + current.state);
							if (!cancelled.current) {
								setCurrent({
									...current,
									state: current.state + 1
								});
							}
						} else {
							setCurrent({
								...current,
								error: 'An error occurred while uploading, the CSV file might be invalid.'
							});	
						}
					}).catch(() => {
						setCurrent({
							...current,
							error: 'A server error occurred while uploading.'
						});	
					});
				} else {
					// something went wrong
					setCurrent({
						...current,
						error: 'The upload process is possibly corrupted, please start over.'
					});
				}
			} else {
				post(`1.0/admin/prices-revision/${current.id}/complete`, { token })
					.then(() => {
						setCurrent({ ...current, complete: true });
					});
			}
		}
	}, [token, current, setCurrent]);

	const onDrop = useCallback((acceptedFiles: File[]) => {
		cancelled.current = false;
		setError('');
		setEntries(entries => {
			const newEntries = [...entries, ...acceptedFiles.map(file => ({ 
				file, 
				states: [...allStates], 
				errors: []
			}))];
			setActiveIndex(newEntries.length - 1);
			return newEntries;
		});
	}, [setEntries, setActiveIndex, setError]);

	const updateStates = useCallback((index: number, states: string[]) => {
		setEntries(entries => {
			const pre = entries.slice(0, index);
			const post = entries.slice(index + 1);
			const subject = {
				...entries[index],
				states
			}
			return [...pre, subject, ...post];
		});
	}, [setEntries]);

	const updateErrors = useCallback((index: number, errors: string[]) => {
		setEntries(entries => {
			const pre = entries.slice(0, index);
			const post = entries.slice(index + 1);
			const subject = {
				...entries[index],
				errors
			}
			return [...pre, subject, ...post];
		});
	}, [setEntries]);

	const onRemove = useCallback((index: number) => {
		setEntries(entries => {
			const pre = entries.slice(0, index);
			const post = entries.slice(index + 1);
			const updatedEntries = [...pre, ...post];
			if (activeIndex >= updatedEntries.length) {
				setActiveIndex(updatedEntries.length - 1);
			}
			return updatedEntries;
		});
	}, [activeIndex, setEntries, setActiveIndex]);

	const onHeaderInputChange = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
		const target = e.target as HTMLInputElement;
		setHeaders(headers => ({
			...headers,
			[target.name]: target.value,
		}));
	}, [setHeaders]);

	const onSubmit = () => {
		if (!token) {
			history.push('/logout');
			return; 
		}

		if (!revisionName) {
			setError('Please provide the revision name.');
			return;
		}

		if (entries.length === 0) {
			setError('No files selected. Please select the CSV file(s).');
			return;
		}

		const expectedHeaders = Object.keys(
			headers
		) as (keyof PricesCSVHeaders)[];
		const assignedHeaders = expectedHeaders.reduce(
			(result, h: keyof PricesCSVHeaders) => {
				return {
					...result,
					[headers[h]]: h,
				};
			},
			{}
		) as { [key: string]: string };
		const excludedHeaders = [] as string[];

		Promise.all(
			entries.map((entry, index) => {
				return new Promise<ParseResult<PricesCSVHeaders>>((resolve, reject) => {
					const results: ParseResult<PricesCSVHeaders> = {
						data: [],
						errors: [],
						meta: {
							aborted: false,
							cursor: 0,
							delimiter: '',
							linebreak: '',
							truncated: false,
							fields: [],
						},
					};
					
					parse(entry.file, {
						header: true,
						dynamicTyping: true,
						skipEmptyLines: 'greedy',
						transformHeader: (header) => {
							if (assignedHeaders[header]) {
								return assignedHeaders[header];
							} else {
								excludedHeaders.push(header);
							}
							return header;
						},
						step: (stepResult) => {
							const result = (stepResult as any) as ParseResultSingle<PricesCSVHeaders>;
							result.data = excludedHeaders.reduce((d, h) => {
								delete d[h as keyof PricesCSVHeaders];
								return d;
							}, result.data);
							results.data.push(result.data);
							if (result.errors.length > 0) {
								results.errors = results.errors.concat(result.errors);
							}
							if (results.meta.fields && results.meta.fields.length === 0) {
								results.meta = result.meta;
							}
						},
						complete: (_) => {
							let errors: string[] = [];
		
							expectedHeaders.every((header) => {
								if (results.meta.fields) {
									if (results.meta.fields.indexOf(header) === -1) {
										errors.push(
											`Header "${headers[header]}" is not found in the CSV`
										);
										return false;
									} else {
										return true;
									}
								}
								return false;
							});
		
							if (results.data && results.data.length === 0) {
								errors.push('No data can be parsed');
							} else if (results.errors && results.errors.length > 0) {
								errors = errors.concat(
									results.errors.map((error) =>
										error.message.replace(`; defaulted to ','`, '')
									)
								);
							}
		
							results.data.find((item) => {
								if (!accepteduom.includes(item.unit_of_measurement)) {
									errors.push(
										`An invalid unit of measurement "${item.unit_of_measurement}" is used by "${item.name}", 
										please use only one of the following: ${accepteduom.join(', ')}`
									);
									return true;
								}
								return false;
							});
		
							if (errors.length > 0) {
								updateErrors(index, errors);
								reject(index);
							} else {
								resolve(results);
							}
						}
					}); // parse
				}); // for each entries
			})
		).then((results) => {

			const allData = entries.reduce<any[]>((all, entry, index) => {
				const states = entry.states.join(',');
				let data = [];
				for (let i = 0; i < results[index].data.length; i++) {
					data[i] = results[index].data[i] as unknown as any;
					data[i].states = states;
					if (data[i].unit_of_measurement?.toUpperCase() === 'E') {
						data[i].unit_of_measurement = 'EA';
					}
				}
				return all.concat(data);
			}, []);

			post('1.0/admin/prices-revision', { token, name: revisionName }).then(
				(result) => {
					if (result.data.id) {
						const length = Math.ceil(allData.length / segmentSize);
						for (let i = 0; i < length; i++) {
							const data = JSON.stringify(allData.slice(segmentSize * i, segmentSize * (i + 1)));
							localStorage.setItem(result.data.id + '_' + i, data);
						}
						setCurrent({
							id : result.data.id,
							name: revisionName,
							state: 0,
							size: length,
							complete: false
						});
						// setFile(null);
					}
				}
			);

		}).catch(() => {
			setError('Please check the CSV files for any errors.');
			return;
		});
	};


	const reset = () => {
		setRevisionName(DateTime.now().toLocaleString(DateTime.DATETIME_FULL));
		setError('');
		setEntries([]);
		setActiveIndex(0);
		cancelled.current = false;
	};

	const handleCancelUpload = async () => {
		reset();
		if (current?.id) {
			cancelled.current = true;
			await post(`1.0/admin/prices-revision/${current.id}/delete`, {token});
			const stateNumber = current?.state ?? -1;
			if (stateNumber > -1) {
				let i = stateNumber;
				if (current?.id) {
					do {
						const currentSegment = window.localStorage.getItem(current.id + '_' + i);
						if (currentSegment) {
							window.localStorage.removeItem(current.id + '_' + i);
						}
						i++;
					} while(i < current.size);
				}
			}
		}
		setCurrent(null);
	};

	const onComplete = async (activate: boolean) => {
		reset();
		if (current) {
			if (activate) {
				await post(`1.0/admin/prices-revision/${current.id}/activate`, {token});	
			}
			history.push(`/admin/prices?highlight=${current.id}`);
			setCurrent(null);
		}
	};

	const onStartOver = async () => {
		reset();
		if (current) {
			setCurrent(null);
		}
	};

  return (
		<div className='newprices-page'>
			<div className="header">
				<h3>New Price Revision</h3>
				{error && <Label basic color='red'>{error}</Label>}
				<Button primary onClick={onSubmit}>
					<Icon name='paper plane' />
					Submit
				</Button>
			</div>
			<div className="content">
				<Input label={'Revision Name'} autoFocus value={revisionName} onChange={(_, d) => setRevisionName(d.value)} />
				<Accordion styled fluid>
					<Accordion.Title active={showHeaders} onClick={() => setShowHeaders(v => !v)}>
						<Icon name='dropdown' />
						CSV Headers (These are the expected values of the CSV headers. Change if needed.)
					</Accordion.Title>
					<Accordion.Content active={showHeaders}>
						<Form>
							<Form.Group widths='equal'>
								<Form.Field>
									<label>Support Item Number</label>
									<Input
										type='text'
										name='external_id'
										value={headers.external_id}
										onChange={onHeaderInputChange}
									/>
								</Form.Field>
								<Form.Field>
									<label>Support Item</label>
									<Input
										type='text'
										name='name'
										value={headers.name}
										onChange={onHeaderInputChange}
									/>
								</Form.Field>
								<Form.Field>
									<label>Support Item Description</label>
									<Input
										type='text'
										name='description'
										value={headers.description}
										onChange={onHeaderInputChange}
									/>
								</Form.Field>
								<Form.Field>
									<label>Support Categories</label>
									<Input
										type='text'
										name='category'
										value={headers.category}
										onChange={onHeaderInputChange}
									/>
								</Form.Field>
							</Form.Group>
							<Form.Group widths='equal'>
								<Form.Field>
									<label>Unit of Measure</label>
									<Input
										type='text'
										name='unit_of_measurement'
										value={headers.unit_of_measurement}
										onChange={onHeaderInputChange}
									/>
								</Form.Field>
								<Form.Field>
									<label>Quote</label>
									<Input
										type='text'
										name='quote'
										value={headers.quote}
										onChange={onHeaderInputChange}
									/>
								</Form.Field>
								<Form.Field>
									<label>Price Limit</label>
									<Input
										type='text'
										name='ndis_price'
										value={headers.ndis_price}
										onChange={onHeaderInputChange}
									/>
								</Form.Field>
								<Form.Field>
									<label>Support Category Number</label>
									<Input
										type='text'
										name='support_area_id'
										value={headers.support_area_id}
										onChange={onHeaderInputChange}
									/>
								</Form.Field>
							</Form.Group>
						</Form>
					</Accordion.Content>
				</Accordion>
				<DropZone options={{ onDrop }}>
					{entries.length > 0 && (
						<Accordion styled fluid>
							{entries.map((entry, i) => <React.Fragment key={`csv_acd_${i}`}>
								<Accordion.Title active={activeIndex === i} onClick={() => setActiveIndex(i)}>
									<Icon name='dropdown' />
									{entry.errors.length > 0 && <Icon name='warning sign' color='red' />}
									{entry.file.name}
								</Accordion.Title>
								<Accordion.Content active={activeIndex === i}>
									<Grid>
										<Grid.Row>
											<Grid.Column>
												<Dropdown fluid multiple search selection options={stateOptions} placeholder={'States'} 
													value={entry.states}
													onChange={(_, d) => updateStates(i, d.value as string[])}
												/>
											</Grid.Column>
										</Grid.Row>
										<Grid.Row>
											<Grid.Column width={12} verticalAlign='middle'>
												{entry.errors.length > 0 && entry.errors.map(error => 
													<Label basic color='red'>{error}</Label>
												)}
											</Grid.Column>
											<Grid.Column textAlign='right' verticalAlign='middle' width={4}>
												<Button color='red' onClick={() => onRemove(i)}>
													<Icon name='trash' />
													Remove
												</Button>
											</Grid.Column>
										</Grid.Row>
									</Grid>
								</Accordion.Content>
							</React.Fragment>)}
						</Accordion>
					)}
				</DropZone>
			</div>

			<Modal open={!!current} onClose={handleCancelUpload} closeOnEscape={false} closeOnDimmerClick={false} size={'tiny'}>
				{current && <>
					<Modal.Header>
						{current.complete ? `${current.name} upload complete` : `Currently uploading ${current.name}`}
					</Modal.Header>
					<Modal.Content>
						{current.error && <Message negative>
							<p>{current.error}</p>
						</Message>}
						<Progress percent={current.state / current.size * 100} indicating success />
					</Modal.Content>
					<Modal.Actions>
						{current.complete ? <>
							<Button onClick={onStartOver}>Submit Another</Button>
							<Button onClick={() => onComplete(false)}>Go Back</Button>
							<Button onClick={() => onComplete(true)} primary>Activate</Button>
						</> : <> 
							<Button onClick={handleCancelUpload}>Cancel</Button>
						</>}
					</Modal.Actions>
				</>}
			</Modal>
    </div>
  );
};

